<template>
  <div class="v2-carts-total">
    <!-- Collapsible card -->
    <div class="v2-carts-total__card">
      <!-- Header row -->
      <div class="v2-carts-total__header">
        <div
          class="v2-carts-total__header-title"
          @click="onClickOrderDetailsToggle"
        >
          Order details
        </div>
        <div class="v2-carts-total__header-actions">
          <template v-if="!orderDetailsCartVisible">
            <!-- Collapsed: show total + place order inline -->
            <div class="v2-carts-total__inline-total">
              <span class="v2-carts-total__inline-label">Total:</span>
              <span class="v2-carts-total__inline-value">
                {{ formatCurrency(Number(totalOrderValue.totalCartsValue), totalOrderValue.currency) }}
              </span>
            </div>
            <V2PlaceOrderButton
              :is-terms-accepted="isTermsAccepted"
              :is-placing-order="isPlacingOrder"
              @on-click-place-order="onPlaceOrderForAllStores"
              @on-click-select-stores-and-place-order="onClickSelectStoresAndPlaceOrder"
            />
          </template>
          <!-- Chevron toggle -->
          <button
            class="v2-carts-total__chevron"
            @click="onClickOrderDetailsToggle"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline
                v-if="orderDetailsCartVisible"
                points="18 15 12 9 6 15"
              />
              <polyline
                v-else
                points="6 9 12 15 18 9"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Collapsible body -->
      <div
        v-if="orderDetailsCartVisible"
        class="v2-carts-total__body"
      >
        <!-- Totals list -->
        <div class="v2-carts-total__totals">
          <div class="v2-carts-total__total-row">
            <span>Total units</span>
            <span>{{ formatNumber(totalOrderValue.totalCartsUnits) || 0 }}</span>
          </div>
          <div class="v2-carts-total__total-row">
            <span>Total costs</span>
            <span>{{ formatCurrency(Number(totalOrderValue.totalCartsValue), totalOrderValue.currency) }}</span>
          </div>
          <div class="v2-carts-total__total-row">
            <span>Total RRP</span>
            <span>{{ formatCurrency(Number(totalOrderValue.totalCartsRRP), totalOrderValue.currency) }}</span>
          </div>

          <div class="v2-carts-total__divider" />

          <div class="v2-carts-total__total-row v2-carts-total__total-row--bold">
            <span>Total</span>
            <span>{{ formatCurrency(Number(totalOrderValue.totalCartsValue), totalOrderValue.currency) }}</span>
          </div>
        </div>

        <!-- Terms & conditions -->
        <div class="v2-carts-total__terms">
          <div class="v2-carts-total__terms-row">
            <label class="v2-carts-total__terms-checkbox">
              <input
                v-model="isTermsAccepted"
                type="checkbox"
                @change="trackTermsCheck($event.target.checked)"
              >
              I Agree with
            </label>
            <span
              class="v2-carts-total__terms-link"
              @click="handleTermsLink"
            >
              terms and conditions
            </span>
          </div>
        </div>

        <!-- Place order buttons -->
        <div class="v2-carts-total__actions">
          <V2PlaceOrderButton
            :is-terms-accepted="isTermsAccepted"
            :is-placing-order="isPlacingOrder"
            @on-click-place-order="onPlaceOrderForAllStores"
            @on-click-select-stores-and-place-order="onClickSelectStoresAndPlaceOrder"
          />
        </div>
      </div>
    </div>

    <!-- Select Stores Modal -->
    <V2SelectStoresModal
      :show="showSelectStoresModal"
      modal-title="Select stores & place order"
      ok-btn-text="Place an order for selected stores"
      :store-list="cartValuesList"
      :show-order-details="true"
      @on-close-modal="onCloseSelectStoresModal"
      @on-change-stores="onChangeStoreSelection"
      @on-click-download="onPlaceOrderWithSelectedStores"
    />

    <!-- Terms Modal -->
    <V2Modal
      :show="showTermsModal"
      title="Terms and Conditions"
      size="xl"
      @close="closeTermsModal"
    >
      <div class="v2-cart-terms">
        <div class="v2-cart-terms__heading">
          GENERAL SALE CONDITIONS
        </div>
        <div
          v-for="section in cartTermsContent"
          :key="section.title"
          class="v2-cart-terms__section"
        >
          <div class="v2-cart-terms__section-title">
            {{ section.title }}
          </div>
          <p
            v-for="point in section.points"
            :key="point"
            class="v2-cart-terms__point"
          >
            {{ point }}
          </p>
        </div>
      </div>
      <template #footer>
        <V2Button
          variant="outline"
          @click="closeTermsModal"
        >
          Close
        </V2Button>
        <V2Button
          variant="primary"
          @click="updateTerms"
        >
          Accept
        </V2Button>
      </template>
    </V2Modal>
  </div>
</template>

<script>
import { utils, constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { formatNumber } from '@/@core/utils/utils'
import { CREATE_MULTIPLE_ORDER } from '@/store/modules/order.module'
import { apiToastError, apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import { UPDATE_CART_ERRORS, UPDATE_CARTS, LOAD_CARTS } from '@/store/modules/checkout-v2.module'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import V2Button from '@/components/v2/V2Button.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import cartTermsContent from '@/views/apps/checkout/cart-terms-content'
import { trackSelectStorePlaceOrderModal, trackTermsModal } from '@/views/apps/checkout-v2/trackMultipleStoreCartHelpers'
import V2PlaceOrderButton from './V2PlaceOrderButton.vue'
import V2SelectStoresModal from './V2SelectStoresModal.vue'

const { formatCurrency } = utils
const { CURRENCY_CONVERSIONS } = c
const CART_WITH_MULTIPLE_STORES = constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES

export default {
  name: 'V2CartsTotal',
  components: {
    V2Button,
    V2Modal,
    V2PlaceOrderButton,
    V2SelectStoresModal,
  },
  props: {
    cartList: {
      type: Array,
      required: true,
    },
    cartValuesList: {
      type: Array,
      required: true,
    },
    totalOrderValue: {
      type: Object,
      default: () => ({
        totalCartsValue: 0,
        totalCartsUnits: 0,
        totalCartsRRP: 0,
        currency: CURRENCY_CONVERSIONS.USD.SYMBOL,
      }),
    },
    onPlaceOrderSuccess: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      isTermsAccepted: false,
      isPlacingOrder: false,
      orderDetailsCartVisible: true,
      showSelectStoresModal: false,
      showTermsModal: false,
      cartTermsContent,
      formatCurrency,
      formatNumber,
    }
  },
  methods: {
    onClickOrderDetailsToggle() {
      this.orderDetailsCartVisible = !this.orderDetailsCartVisible
      analytics.track(
        this.orderDetailsCartVisible
          ? CART_WITH_MULTIPLE_STORES.RETAILER_EXPANDS_ORDER_DETAILS_CARD
          : CART_WITH_MULTIPLE_STORES.RETAILER_COLLAPSES_ORDER_DETAILS_CARD,
        { totalOrderValue: this.totalOrderValue },
      )
    },
    trackTermsCheck(isChecked) {
      analytics.track(
        constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_TERMS_CONDITIONS_CHECKBOX,
        { carts: this.cartList, isChecked },
      )
    },
    handleTermsLink() {
      this.showTermsModal = true
      trackTermsModal('open', this.cartList, this.isTermsAccepted)
    },
    closeTermsModal() {
      this.showTermsModal = false
      trackTermsModal('close', this.cartList, this.isTermsAccepted)
    },
    updateTerms() {
      this.isTermsAccepted = true
      this.showTermsModal = false
      trackTermsModal('confirm', this.cartList, this.isTermsAccepted)
    },
    onClickSelectStoresAndPlaceOrder() {
      const storeIds = this.cartList.map(cart => cart._id)
      this.showSelectStoresModal = true
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_SELECT_STROES_AND_PLACE_ORDER_BUTTON, { carts: this.cartValuesList, storeIds })
    },
    onCloseSelectStoresModal() {
      const storeIds = this.cartList.map(cart => cart._id)
      this.showSelectStoresModal = false
      trackSelectStorePlaceOrderModal('close', this.cartList, storeIds)
    },
    onChangeStoreSelection(selectedStores) {
      const selectedStoreIds = selectedStores.map(store => store.storeId || store._id || store)
      trackSelectStorePlaceOrderModal('change-stores', this.cartList, selectedStoreIds)
    },
    onPlaceOrderForAllStores() {
      const storeIds = this.cartList.map(cart => cart._id)
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_PLACE_ORDER_FOR_ALL_STORES_BUTTON, { carts: this.cartValuesList, storeIds })
      this.placeOrder(storeIds)
    },
    onPlaceOrderWithSelectedStores(selectedStores = []) {
      if (!selectedStores.length) {
        apiToastWarning('Please select at least one store to place the order')
        return
      }
      // V2SelectStoresModal passes store objects; extract storeId values
      const storeIds = selectedStores.map(store => store.storeId || store._id || store)
      trackSelectStorePlaceOrderModal('confirm', this.cartList, storeIds)
      this.showSelectStoresModal = false
      this.placeOrder(storeIds)
    },
    async placeOrder(storeIds = []) {
      try {
        this.startPlacingOrder()
        await this.updateCarts()
        const res = await this.createMultipleOrders(storeIds)
        this.handleOrderSuccess(res, storeIds)
      } catch (err) {
        this.handleOrderError(err)
      } finally {
        this.finishPlacingOrder()
      }
    },
    startPlacingOrder() {
      this.isPlacingOrder = true
    },
    finishPlacingOrder() {
      this.isPlacingOrder = false
    },
    async updateCarts() {
      await this.$store.dispatch(UPDATE_CARTS)
    },
    async createMultipleOrders(storeIds) {
      return await this.$store.dispatch(CREATE_MULTIPLE_ORDER, { cartIds: storeIds })
    },
    handleOrderSuccess(res, storeIds) {
      const message = res.data.message || 'Order placed successfully'
      apiToastSuccess(message)
      analytics.track(CART_WITH_MULTIPLE_STORES.ORDER_PLACED_SUCCESSFULLY, { carts: this.cartValuesList, cartIds: storeIds })
      this.onPlaceOrderSuccess()
      this.setOrderSuccessPopupContext(res?.data?.data)
    },
    setOrderSuccessPopupContext(data) {
      this.$root.$orderSuccessPopupContext.value = {
        orderIds: data || [],
      }
    },
    handleOrderError(err) {
      const errorData = err?.response?.data?.data
      this.processSucceededOrders(errorData?.succeededOrders ?? [])
      this.processFailedStoreOrders(errorData?.failedStoreOrders ?? [], err)
    },
    processSucceededOrders(succeededOrders) {
      if (succeededOrders.length) {
        this.setOrderSuccessPopupContext(succeededOrders)
      }
    },
    processFailedStoreOrders(failedStoreOrders, err) {
      if (failedStoreOrders.length) {
        this.showFailedStoreOrdersErrors(failedStoreOrders)
        this.$store.commit(UPDATE_CART_ERRORS)
        this.$store.dispatch(LOAD_CARTS)
      } else {
        apiToastError(err)
      }
      analytics.track(CART_WITH_MULTIPLE_STORES.GETTING_ERROR_WHILE_PLACING_ORDER, err?.response)
    },
    showFailedStoreOrdersErrors(failedStoreOrders) {
      failedStoreOrders.forEach(errObj => {
        apiToastError(`${errObj?.storeName} - ${errObj?.error}`)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-carts-total {
  margin-top: 0.5rem;

  &__card {
    background: var(--v2-muted);
    border-radius: var(--v2-radius-lg);
    padding: 1rem;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  &__header-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--v2-foreground);
    cursor: pointer;
    font-family: var(--v2-font-display);
    flex-shrink: 0;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    justify-content: flex-end;
  }

  &__inline-total {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
  }

  &__inline-label {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }

  &__inline-value {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-primary);
  }

  &__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: var(--v2-foreground);
    cursor: pointer;
    padding: 0.125rem;
    border-radius: var(--v2-radius-sm);
    flex-shrink: 0;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  }

  &__body {
    margin-top: 1rem;
  }

  &__totals {
    margin-bottom: 1rem;
  }

  &__total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    padding: 0.375rem 0;

    span:last-child {
      color: var(--v2-foreground);
      font-weight: 500;
    }

    &--bold {
      font-size: 1rem;
      font-weight: 700;

      span {
        color: var(--v2-foreground) !important;
      }
    }
  }

  &__divider {
    height: 1px;
    background: var(--v2-border);
    margin: 0.5rem 0;
  }

  &__terms {
    margin: 0.75rem 0;
  }

  &__terms-row {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-wrap: wrap;
  }

  &__terms-checkbox {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.875rem;
    color: var(--v2-foreground);
    cursor: pointer;
    margin-bottom: 0;

    input[type='checkbox'] {
      width: 16px;
      height: 16px;
      accent-color: var(--v2-primary);
      cursor: pointer;
    }
  }

  &__terms-link {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: var(--v2-primary);
    }
  }

  &__actions {
    margin-top: 1rem;
  }
}
</style>
